import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaRegCalendarCheck } from "react-icons/fa6";
import { FiSearch } from "react-icons/fi";
import { MdOutlineLocationOn } from "react-icons/md";
import Handler from "../../../Helpers/Handler";
import { formatDateTime, textCapitalize } from "../../../Helpers/Utils";
import { Skeleton } from "../../../Components/Needed";
import Pagination from "../../../Components/List/Pagination";
import DatePicker from "react-multi-date-picker";
import useListParams from "../../../Helpers/useListParams";

const STATUSES = [
    { key: "PRESENT", label: "Present", tone: "ok" },
    { key: "ABSENT", label: "Absent", tone: "bad" },
    { key: "HALF_DAY", label: "Half day", tone: "warn" },
    { key: "LEAVE", label: "Leave", tone: "info" },
    { key: "WEEKOFF", label: "Week off", tone: "muted" },
    { key: "HOLIDAY", label: "Holiday", tone: "muted" },
];

const dayOffset = (from, to) => {
    if (!from || !to) return 0;
    const a = new Date(from);
    const b = new Date(to);
    a.setHours(0, 0, 0, 0);
    b.setHours(0, 0, 0, 0);
    return Math.round((b - a) / 86400000);
};

const OutTime = ({ checkIn, checkOut }) => {
    if (!checkOut) return "-";
    const offset = dayOffset(checkIn, checkOut);
    return (
        <>{formatDateTime(checkOut)}{offset > 0 ? <span className="att-nextday" title="Next day">+{offset}d</span> : null}</>
    );
};

const toInputDate = (value) => new Date(value).toISOString().slice(0, 10);

const Attendance = () => {
    const navigate = useNavigate();
    const [hubs, setHubs] = useState([]);
    // Page number in the URL path, filters in the query string - see Helpers/useListParams.
    const { page, setPage, filters, setFilter } = useListParams({ date: toInputDate(new Date()), hubId: "", search: "", status: "" });
    const { date, hubId, search, status } = filters;
    const setDate = (value) => setFilter("date", typeof value === "function" ? value(date) : value);
    const setHubId = (value) => setFilter("hubId", typeof value === "function" ? value(hubId) : value);
    const setSearch = (value) => setFilter("search", typeof value === "function" ? value(search) : value);
    const setStatus = (value) => setFilter("status", typeof value === "function" ? value(status) : value);
    const [count] = useState(10);
    const [data, setData] = useState({ list: [], total: 0, summary: {} });
    const [loading, setLoading] = useState(true);
    const toneOf = (status) => STATUSES.find((item) => item.key === status)?.tone || "muted";
    const labelOf = (status) => STATUSES.find((item) => item.key === status)?.label || textCapitalize(status || "-");

    const asHours = (minutes) => {
        if (!minutes) return "-";
        const hours = Math.floor(minutes / 60);
        const rest = minutes % 60;
        return hours ? `${hours}h ${rest}m` : `${rest}m`;
    };

    useEffect(() => {
        (async () => {
            const response = await Handler({ method: "GET", url: "/admin/hub/active" });
            setHubs(response?.success ? response?.data?.data || [] : []);
        })();
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        const response = await Handler({
            method: "post",
            url: `/admin/staff/attendance/list/${page - 1}?count=${count}`,
            data: {
                date: new Date(date).toISOString(),
                ...(hubId ? { hubId } : {}),
                ...(search.trim() ? { search: search.trim() } : {}),
                ...(status ? { status } : {}),
            },
        });
        setData(response?.success ? response?.data?.data || { list: [], total: 0, summary: {} } : { list: [], total: 0, summary: {} });
        setLoading(false);
    }, [date, hubId, search, status, page, count]);

    useEffect(() => { load(); }, [load]);
    // Changing a filter goes back to page 1 inside setFilter (useListParams), so a page restored from the URL is kept.

    const summary = useMemo(() => data?.summary || {}, [data]);
    const onShift = data?.list?.filter((row) => row.open).length || 0;

    return (
        <div className="container-fluid px-0">
            {/* <button type="button" className="btn btn-link text-decoration-none px-0 mb-2 d-inline-flex align-items-center gap-2" onClick={() => navigate("/staff")}><FaArrowLeft /> Back to staff</button> */}
            <div className="card border-0 shadow-sm rounded-4 mb-3 shift-hero">
                <div className="card-body p-4">
                    <h3 className="fw-bold mb-1 d-flex align-items-center gap-2"><FaRegCalendarCheck className="text-primary" /> Attendance
                        {/* <button type="button" className="btn btn-sm btn-outline-primary ms-auto" onClick={() => navigate("/attendance/report")}>Report</button> */}
                    </h3>
                    <p className="text-muted small mb-3">Who was in on a given day. Anyone with no record for the date counts as absent.</p>
                    <div className="row g-3 align-items-end">
                        <div className="col-6 col-md-3 col-xl-2">
                            <label className="form-label small fw-semibold">Date</label>
                            {/* <input type="date" className="form-control"  max={toInputDate(new Date())} /> */}
                            <div>
                                <DatePicker maxDate={new Date()} inputClass="form-control" value={new Date(date)} onChange={(e) => setDate(new Date(e))} format="DD/MM/YYYY" />
                            </div>
                        </div>
                        <div className="col-6 col-md-3 col-xl-3">
                            <label className="form-label small fw-semibold d-flex align-items-center gap-1"><MdOutlineLocationOn /> Hub</label>
                            <select className="form-select" value={hubId} onChange={(e) => setHubId(e.target.value)}>
                                <option value="">All hubs</option>
                                {hubs.map((hub) => <option key={hub.hubId} value={hub.hubId}>{hub.hubId}{hub.area ? ` - ${hub.area}` : ""}</option>)}
                            </select>
                        </div>
                        <div className="col-12 col-md-3 col-xl-3">
                            <label className="form-label small fw-semibold">Status</label>
                            <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                                <option value="">All</option>
                                {STATUSES.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}
                            </select>
                        </div>
                        {/* <div className="col-12 col-md-3 col-xl-4">
                            <label className="form-label small fw-semibold">Search</label>
                            <div className="role-search">
                                <FiSearch />
                                <input type="text" className="form-control" placeholder="Name, staff ID or employee ID..." value={search} onChange={(e) => setSearch(e.target.value)} />
                            </div>
                        </div> */}
                    </div>
                    <div className="d-flex flex-wrap gap-2 mt-3">
                        {STATUSES.map((item) => (
                            <span key={item.key} className={`att-chip att-${item.tone}`}>
                                {item.label}<strong className="ms-2">{summary[item.key] || 0}</strong>
                            </span>
                        ))}
                        <span className="att-chip att-live">Still on shift<strong className="ms-2">{onShift}</strong></span>
                    </div>
                </div>
            </div>
            <div className="card border-0 shadow-sm rounded-4">
                <div className="card-body">
                    {loading ? <Skeleton height={320} className="rounded-4" /> : !data?.list?.length ? (
                        <div className="text-center py-5 text-muted">No staff match these filters.</div>
                    ) : (
                        <>
                            <div className="table-responsive">
                                <table className="table align-middle mb-0">
                                    <thead>
                                        <tr className="text-muted small">
                                            <th>Staff</th>
                                            <th>Shift</th>
                                            <th>Check in</th>
                                            <th>Check out</th>
                                            <th>Worked</th>
                                            <th>Break</th>
                                            <th>Status</th>
                                            {/* <th className="text-end">Action</th> */}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.list.map((row) => (
                                            <tr key={row.staffId} className="att-row" onClick={() => navigate(`/attendance/${row.staffId}`)}>
                                                <td>
                                                    <div className="fw-medium text-capitalize d-flex align-items-center gap-2">
                                                        {textCapitalize(row.name)}
                                                        {row.carriedOver ? <span className="att-chip att-live">Night shift, started {formatDateTime(row.shiftStartedOn)?.split(",")[0]}</span> : null}
                                                    </div>
                                                    <div className="text-muted fs-8 font-monospace">{row.employeeId || row.staffId}</div>
                                                </td>
                                                <td className="small text-capitalize">
                                                    {row.shift || "-"}
                                                    {row.sessionsCount > 1 ? <span className="att-chip att-muted ms-1" title="More than one shift today">{row.sessionsCount} shifts</span> : null}
                                                </td>
                                                <td className="small">{row.checkIn ? formatDateTime(row.checkIn) : "-"}</td>
                                                <td className="small">
                                                    {row.checkOut ? <OutTime checkIn={row.checkIn} checkOut={row.checkOut} /> : row.open ? <span className="att-open">On shift</span> : "-"}
                                                </td>
                                                <td className="small">{asHours(row.totalWorkingMinutes)}</td>
                                                <td className="small">{asHours(row.totalBreakMinutes)}</td>
                                                <td><span className={`att-pill att-${toneOf(row.status)}`}>{labelOf(row.status)}</span></td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="mt-3">
                                <Pagination current={page} total={data.total} pageSize={count} onPageChange={setPage} />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Attendance;
